import { supportsClientCreation } from './clientOnboardingExecution';
export type FrontendMode = 'aws' | 'demo';
export type FrontendDeploymentStage = 'dev' | 'test' | 'prod' | null;

export const FRONTEND_MODE: FrontendMode =
  process.env.REACT_APP_FRONTEND_MODE === 'aws' ? 'aws' : 'demo';

const rawDeploymentStage = (process.env.REACT_APP_DEPLOYMENT_ENV ?? '').trim().toLowerCase();
export const FRONTEND_DEPLOYMENT_STAGE: FrontendDeploymentStage =
  rawDeploymentStage === 'dev' || rawDeploymentStage === 'test' || rawDeploymentStage === 'prod'
    ? rawDeploymentStage
    : null;

export const IS_AWS_MODE = FRONTEND_MODE === 'aws';
export const IS_DEV_DEPLOYMENT = FRONTEND_DEPLOYMENT_STAGE === 'dev';
export const FRONTEND_MODE_BADGE_KEY = IS_AWS_MODE ? 'common:modeBadge_aws' : 'common:modeBadge_demo';

export const AWS_ADMIN_PATH = '/admin';
export const AWS_CLIENTS_PATH = '/admin/clients';
export const AWS_CLIENT_ONBOARDING_PATH = '/admin/clients/new';
export const AWS_ADMIN_USERS_PATH = '/admin/users';
export const AWS_ADMIN_COMMISSIONING_PATH = '/admin/commissioning';

// AGRISAFE-R1: AWS mode exposes the validated field surface.
// ADMIN-SHELL-R1 additionally exposes only the AWS SuperAdmin surfaces that are
// backed by canonical APIs and have truthful UI contracts.
export const AWS_NAVIGATION_PATHS = [
  '/dashboard',
  '/sectors',
  '/pilotage',
  '/pumping',
  '/iot',
  '/devices',
  '/alerts',
  '/documentation',
  AWS_ADMIN_PATH,
  AWS_CLIENTS_PATH,
  AWS_ADMIN_USERS_PATH,
  AWS_ADMIN_COMMISSIONING_PATH,
] as const;

export const AWS_FIELD_NAVIGATION_PATHS = [
  '/dashboard',
  '/sectors',
  '/pilotage',
  '/pumping',
  '/devices',
] as const;

const AWS_ONLY_PATHS: readonly string[] = [
  AWS_CLIENTS_PATH,
  AWS_CLIENT_ONBOARDING_PATH,
  AWS_ADMIN_COMMISSIONING_PATH,
  '/sectors',
  '/pilotage',
  '/pumping',
];

export const isPathEnabled = (path: string, mode: FrontendMode = FRONTEND_MODE): boolean => {
  if (path === AWS_CLIENT_ONBOARDING_PATH) return mode === 'aws' && supportsClientCreation();
  if (mode === 'demo') {
    return !AWS_ONLY_PATHS.includes(path);
  }
  return (AWS_NAVIGATION_PATHS as readonly string[]).includes(path);
};

/**
 * Client onboarding is a platform-wide administration capability.
 * Keep this fail-closed: organization-scoped admin aliases (coop_admin,
 * org_admin, admin) must never be treated as platform superadmins.
 */
export const isClientOnboardingAdminRole = (role?: string | null): boolean =>
  role === 'superadmin';

export const getAuthenticatedHomePath = (
  role?: string | null,
  mode: FrontendMode = FRONTEND_MODE,
): string => (mode === 'aws' && role === 'superadmin' ? AWS_ADMIN_PATH : '/dashboard');

const ADMIN_NAVIGATION_PATH_BY_MODE: Record<FrontendMode, string> = {
  aws: AWS_ADMIN_PATH,
  demo: '/admin',
};

export const getAdminNavigationPath = (mode: FrontendMode = FRONTEND_MODE): string =>
  ADMIN_NAVIGATION_PATH_BY_MODE[mode];

export const isTtnSimulatorEnabled = (
  value = process.env.REACT_APP_ENABLE_TTN_SIMULATOR,
): boolean => value === 'true';

export const isTelemetryEnabled = (
  value = process.env.REACT_APP_TELEMETRY_BACKEND,
): boolean => value !== 'disabled';

export const supportsValveControl = (model: string): boolean =>
  model === 'valve_water_flow';
